import { useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import { createBeforeAfterComparison } from "../../../../../services/consults-utils";
import { trackEvent } from "../../../../../utils/mixpanel";
import simulationService from "../../../../../services/simulation";
import type { TConsultView, TSession } from "../../../../../utils/types";
import type { ConsultPatient } from "./useConsultSearch";
import type { GalleryImageMetadata } from "../../../../common/native/GalleryModal";

interface PhotoGalleryProps {
  selectedPatient: ConsultPatient | null;
  setSelectedPhotos: Dispatch<SetStateAction<string[]>>;
  setSelectedPhotoThumbnails?: Dispatch<SetStateAction<string[]>>;
  setSelectedPatientName: Dispatch<SetStateAction<string>>;
  setGalleryModalVisible: Dispatch<SetStateAction<boolean>>;
  setSelectedPhotoEditTargets?: Dispatch<
    SetStateAction<
      Array<{ targetType: "clinic_patient_image"; targetId: number } | null>
    >
  >;
  setSelectedPhotoMetadata?: Dispatch<
    SetStateAction<Array<GalleryImageMetadata | null>>
  >;
  setLoading: Dispatch<SetStateAction<boolean>>;
  session: TSession | null;
  currentView: TConsultView;
}

/**
 * Manages photo gallery and simulation image handling
 */
export const usePhotoGallery = ({
  selectedPatient,
  setSelectedPhotos,
  setSelectedPhotoThumbnails,
  setSelectedPatientName,
  setGalleryModalVisible,
  setSelectedPhotoEditTargets,
  setSelectedPhotoMetadata,
  setLoading,
  session,
  currentView,
}: PhotoGalleryProps) => {
  // Handle simulation tap to show before/after comparison
  const handleSimulationTap = useCallback(
    async (simulation: any) => {
      const patientName = selectedPatient?.name || "Patient";
      try {
        trackEvent(
          "Simulation Viewed",
          {
            "Patient ID": selectedPatient?.clinicOSUUID,
            "Patient Name": patientName,
            "Simulation ID": simulation.id,
            "Procedure Type": simulation.procedureType,
            "View Mode": currentView,
          },
          session ?? undefined,
        );

        // Create comparison if we have both images
        if (simulation.originalImageUrl && simulation.imageUrl) {
          const combinedImageUrl = await createBeforeAfterComparison(
            simulation.originalImageUrl,
            simulation.imageUrl,
          );

          setSelectedPhotos([combinedImageUrl]);
          setSelectedPhotoThumbnails?.([]);
          setSelectedPhotoEditTargets?.([null]);
          setSelectedPhotoMetadata?.([]);
          setSelectedPatientName(
            `${patientName} - ${simulation.procedureType} Comparison`,
          );
        } else {
          // Just show the simulation
          setSelectedPhotos([simulation.imageUrl]);
          setSelectedPhotoThumbnails?.([]);
          setSelectedPhotoEditTargets?.([null]);
          setSelectedPhotoMetadata?.([]);
          setSelectedPatientName(
            `${patientName} - ${simulation.procedureType}`,
          );
        }

        setGalleryModalVisible(true);
      } catch (error) {
        console.error("Error creating comparison image:", error);

        // Fallback on error
        setSelectedPhotos([simulation.imageUrl]);
        setSelectedPhotoThumbnails?.([]);
        setSelectedPhotoEditTargets?.([null]);
        setSelectedPhotoMetadata?.([]);
        setSelectedPatientName(`${patientName} - ${simulation.procedureType}`);
        setGalleryModalVisible(true);
      }
    },
    [
      selectedPatient,
      currentView,
      session,
      setSelectedPhotos,
      setSelectedPhotoThumbnails,
      setSelectedPatientName,
      setGalleryModalVisible,
      setSelectedPhotoEditTargets,
      setSelectedPhotoMetadata,
    ],
  );

  // Fetch starred simulations for presentation
  const fetchStarredSimulationsForPresentation = useCallback(async () => {
    if (!selectedPatient?.clinicOSUUID || !session?.clinicId) {
      console.log("Missing required data to fetch starred simulations");
      return [];
    }

    try {
      setLoading(true);

      const starredSims = await simulationService.getStarredSimulationImages(
        session,
        session.clinicId,
        selectedPatient.clinicOSUUID,
      );

      if (starredSims && starredSims.length > 0) {
        const formattedSimulations = starredSims
          .map((item) => {
            const imageData = item.image_data;

            const beforeUrl =
              item.presigned_urls && item.presigned_urls.length > 0
                ? item.presigned_urls[0]
                : null;

            const afterUrl =
              item.presigned_urls && item.presigned_urls.length > 1
                ? item.presigned_urls[1]
                : item.presigned_urls?.[0] || null;

            if (!afterUrl) return null;

            const procedureType =
              imageData.procedure_types_human_readable?.[0] ||
              imageData.procedure_types?.[0] ||
              "Unknown";

            return {
              id: `sim-${imageData.id}`,
              imageUrl: afterUrl,
              originalImageUrl: beforeUrl,
              procedureType: procedureType,
              view: imageData.view_type_human_readable || "Front View",
              isStarred: true,
              imageId: imageData.id,
            };
          })
          .filter((sim) => sim !== null);

        return formattedSimulations;
      }
      return [];
    } catch (error) {
      console.error("Error fetching starred simulations:", error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [selectedPatient, session, setLoading]);

  // Handle photo view
  const handlePhotoView = useCallback(
    (imageUrl: string, caption?: string) => {
      trackEvent(
        "Patient Photo Viewed",
        {
          "Patient ID": selectedPatient?.clinicOSUUID,
          "Patient Name": selectedPatient?.name,
          "Photo View": caption || "Unknown",
        },
        session ?? undefined,
      );

      if (!imageUrl) {
        console.error("No image URL found");
        return;
      }

      setSelectedPhotos([imageUrl]);
      setSelectedPhotoThumbnails?.([]);
      setSelectedPhotoEditTargets?.([null]);
      setSelectedPhotoMetadata?.([]);
      setSelectedPatientName(selectedPatient?.name || "Patient");
      setGalleryModalVisible(true);
    },
    [
      selectedPatient,
      session,
      setSelectedPhotos,
      setSelectedPhotoThumbnails,
      setSelectedPatientName,
      setGalleryModalVisible,
      setSelectedPhotoEditTargets,
      setSelectedPhotoMetadata,
    ],
  );

  return {
    handleSimulationTap,
    fetchStarredSimulationsForPresentation,
    handlePhotoView,
  };
};
